(() => {
  if (window.customCollectionTabs) {
    window.customCollectionTabs.refresh();
    return;
  }
  const selected = new Map();
  const carousels = new Map();
  const suppressed = new WeakMap();
  let groups = new Map();
  let pointer = null;
  let refreshFrame = 0;
  let resizeFrame = 0;
  const normalize = value => String(value || '').trim().toLowerCase();
  const wrapper = node => node.closest('.shopify-section') || node;
  const available = node => !node.closest('[data-cc-unloaded]');
  const pause = panel => {
    panel.querySelectorAll('video').forEach(video => video.pause());
    panel.dispatchEvent(new CustomEvent('custom-collection:hide', { bubbles: true }));
  };
  const refreshLayout = () => {
    if (resizeFrame) return;
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = 0;
      window.dispatchEvent(new Event('resize'));
    });
  };
  const activate = (groupName, key, options = {}) => {
    const group = groups.get(groupName);
    if (!group || !group.keys.has(key)) return false;
    selected.set(groupName, key);
    for (const panel of group.panels) {
      const visible = normalize(panel.marker.dataset.optionKey) === key;
      if (!visible && !panel.root.hasAttribute('data-cc-hidden')) pause(panel.root);
      panel.root.toggleAttribute('data-cc-hidden', !visible);
      if (visible) panel.root.removeAttribute('aria-hidden');
      else panel.root.setAttribute('aria-hidden', 'true');
    }
    for (const controller of group.controllers) {
      controller.querySelectorAll('[data-cc-switch]').forEach(button => {
        const active = normalize(button.dataset.optionKey) === key;
        button.setAttribute('aria-pressed', String(active));
        button.closest('[data-cc-card-key]')?.classList.toggle('is-cc-selected', active);
        const targets = group.panels.filter(panel => normalize(panel.marker.dataset.optionKey) === normalize(button.dataset.optionKey));
        if (targets.length) button.setAttribute('aria-controls', targets.map(panel => panel.root.id).filter(Boolean).join(' '));
        else button.removeAttribute('aria-controls');
      });
      const announcement = controller.querySelector('[data-cc-announcement]');
      if (announcement) {
        const label = [...controller.querySelectorAll('[data-cc-switch]')].find(button => normalize(button.dataset.optionKey) === key)?.textContent.trim() || key;
        if (announcement.textContent !== label) announcement.textContent = label;
      }
    }
    if (options.user) document.dispatchEvent(new CustomEvent('custom-collection:change', { detail: { group: groupName, option: key } }));
    refreshLayout();
    return true;
  };
  const step = (groupName, direction) => {
    const group = groups.get(groupName);
    if (!group || group.keys.size < 2) return false;
    const keys = [...group.keys];
    const index = Math.max(0, keys.indexOf(selected.get(groupName)));
    return activate(groupName, keys[(index + direction + keys.length) % keys.length], { user: true });
  };
  const initCarousel = section => {
    if (carousels.has(section)) return;
    const track = section.querySelector('[data-cic-track]');
    if (!track) return;
    const abort = new AbortController();
    const signal = abort.signal;
    const previous = section.querySelector('[data-cic-previous]');
    const next = section.querySelector('[data-cic-next]');
    const progress = section.querySelector('[data-cic-progress]');
    const state = { abort, observer: null, timeout: null };
    carousels.set(section, state);
    const cardStep = () => {
      const card = track.querySelector('.category-image-carousel__card');
      if (!card) return track.clientWidth;
      const styles = getComputedStyle(track);
      return card.getBoundingClientRect().width + parseFloat(styles.columnGap || styles.gap || 0);
    };
    const updateControls = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      if (previous) previous.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
      if (progress) {
        const visibleRatio = Math.min(1, track.clientWidth / Math.max(1, track.scrollWidth));
        const thumbWidth = visibleRatio * 100;
        const travel = 100 - thumbWidth;
        const position = maxScroll > 0 ? track.scrollLeft / maxScroll * travel : 0;
        progress.style.width = `${thumbWidth}%`;
        progress.style.transform = `translateX(${position / Math.max(visibleRatio, .01)}%)`;
      }
    };
    previous?.addEventListener('click', () => track.scrollBy({ left: -cardStep(), behavior: 'smooth' }), { signal });
    next?.addEventListener('click', () => track.scrollBy({ left: cardStep(), behavior: 'smooth' }), { signal });
    track.addEventListener('scroll', updateControls, { passive: true, signal });
    window.addEventListener('resize', updateControls, { signal });
    updateControls();
    if (section.hasAttribute('data-home-reveal')) {
      const items = [...section.querySelectorAll('.home-reveal-item')];
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (items.length && !reducedMotion && 'IntersectionObserver' in window) {
        section.classList.add('home-reveal-ready');
        state.observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          state.observer.disconnect();
          section.classList.add('home-reveal-visible');
          const maxOrder = items.reduce((maximum, item) => Math.max(maximum, parseFloat(item.style.getPropertyValue('--home-reveal-order')) || 0), 0);
          state.timeout = setTimeout(() => section.classList.remove('home-reveal-ready', 'home-reveal-visible'), 1050 + maxOrder * 120 + 180);
        }, { threshold: 0.24, rootMargin: '0px 0px -12% 0px' });
        state.observer.observe(section);
      }
    }
  };
  const refresh = () => {
    refreshFrame = 0;
    for (const [section, state] of carousels) {
      if (!section.isConnected || !available(section)) {
        state.abort.abort();
        state.observer?.disconnect();
        clearTimeout(state.timeout);
        carousels.delete(section);
      }
    }
    document.querySelectorAll('[data-category-image-carousel]').forEach(section => {
      if (available(section)) initCarousel(section);
    });
    const nextGroups = new Map();
    document.querySelectorAll('[data-custom-collection-switcher]').forEach(controller => {
      if (!available(controller)) return;
      const name = normalize(controller.dataset.switchGroup);
      if (!name) return;
      if (!nextGroups.has(name)) nextGroups.set(name, { controllers: [], panels: [], keys: new Set() });
      const group = nextGroups.get(name);
      group.controllers.push(controller);
      controller.querySelectorAll('[data-cc-switch]').forEach(button => {
        const key = normalize(button.dataset.optionKey);
        if (key && key !== 'none') group.keys.add(key);
      });
    });
    document.querySelectorAll('[data-custom-collection-panel]').forEach(marker => {
      const root = wrapper(marker);
      if (!available(root)) return;
      const name = normalize(marker.dataset.switchGroup);
      const group = nextGroups.get(name);
      const key = normalize(marker.dataset.optionKey);
      if (group && key && key !== 'none') group.panels.push({ marker, root });
      else {
        root.removeAttribute('data-cc-hidden');
        root.removeAttribute('aria-hidden');
      }
    });
    groups = nextGroups;
    document.querySelectorAll('[data-cc-navigation]').forEach(navigation => {
      const group = groups.get(normalize(navigation.dataset.switchGroup));
      navigation.hidden = !available(navigation) || !group || group.keys.size < 2;
    });
    for (const [name, group] of groups) {
      if (!group.keys.size) continue;
      const requested = selected.get(name) || normalize(group.controllers[0].dataset.defaultOption);
      activate(name, group.keys.has(requested) ? requested : group.keys.values().next().value);
    }
  };
  const scheduleRefresh = () => {
    if (!refreshFrame) refreshFrame = requestAnimationFrame(refresh);
  };
  document.addEventListener('pointerdown', event => {
    const controller = event.target.closest('[data-custom-collection-switcher]');
    const track = event.target.closest('[data-cic-track], [data-cc-options]');
    pointer = controller && track ? { controller, track, id: event.pointerId, x: event.clientX, y: event.clientY, scroll: track.scrollLeft, moved: false } : null;
    if (controller) suppressed.delete(controller);
  }, { passive: true });
  document.addEventListener('pointermove', event => {
    if (pointer && event.pointerId === pointer.id && Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 10) pointer.moved = true;
  }, { passive: true });
  const finishPointer = event => {
    if (!pointer || event.pointerId !== pointer.id) return;
    if (pointer.moved || Math.abs(pointer.track.scrollLeft - pointer.scroll) > 8) suppressed.set(pointer.controller, performance.now() + 500);
    pointer = null;
  };
  document.addEventListener('pointerup', finishPointer, { passive: true });
  document.addEventListener('pointercancel', finishPointer, { passive: true });
  document.addEventListener('click', event => {
    const arrow = event.target.closest('[data-cc-step]');
    if (arrow) {
      const navigation = arrow.closest('[data-cc-navigation]');
      if (!navigation || navigation.hidden || !available(navigation)) return;
      const name = normalize(navigation.dataset.switchGroup);
      const direction = arrow.dataset.ccStep === '-1' ? -1 : 1;
      event.preventDefault();
      const focused = arrow.contains(document.activeElement);
      if (step(name, direction) && focused) {
        const panel = groups.get(name).panels.find(item => normalize(item.marker.dataset.optionKey) === selected.get(name) && item.root.querySelector('[data-cc-navigation]'));
        const target = panel?.root.querySelector(`[data-cc-step="${direction}"]`) || groups.get(name).controllers[0].querySelector(`[data-cc-switch][data-option-key="${selected.get(name)}"]`);
        target?.focus({ preventScroll: true });
      }
      return;
    }
    const card = event.target.closest('[data-cc-card-key]');
    const controller = card?.closest('[data-custom-collection-switcher]');
    if (!card || !controller || event.target.closest('[data-cic-previous], [data-cic-next]')) return;
    if (event.detail !== 0 && (suppressed.get(controller) || 0) > performance.now()) {
      event.preventDefault();
      return;
    }
    const key = normalize(card.dataset.ccCardKey);
    if (key === 'none' || !key) return;
    event.preventDefault();
    activate(normalize(controller.dataset.switchGroup), key, { user: true });
  });
  document.addEventListener('keydown', event => {
    const button = event.target.closest('[data-cc-options] [data-cc-switch]');
    if (!button || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const controller = button.closest('[data-custom-collection-switcher]');
    const name = normalize(controller.dataset.switchGroup);
    const group = groups.get(name);
    if (!group?.keys.size) return;
    event.preventDefault();
    const keys = [...group.keys];
    if (event.key === 'Home' || event.key === 'End') activate(name, event.key === 'Home' ? keys[0] : keys[keys.length - 1], { user: true });
    else step(name, event.key === 'ArrowLeft' ? -1 : 1);
    controller.querySelector(`[data-cc-switch][data-option-key="${selected.get(name)}"]`)?.focus({ preventScroll: true });
  });
  const revealEditorSelection = event => {
    const root = wrapper(event.target);
    const marker = event.target.closest('[data-custom-collection-panel]') || root.querySelector('[data-custom-collection-panel]');
    refresh();
    if (marker) activate(normalize(marker.dataset.switchGroup), normalize(marker.dataset.optionKey));
    const card = event.target.closest('[data-cc-card-key]');
    const controller = card?.closest('[data-custom-collection-switcher]');
    if (controller) activate(normalize(controller.dataset.switchGroup), normalize(card.dataset.ccCardKey));
  };
  document.addEventListener('shopify:section:load', event => {
    event.target.removeAttribute('data-cc-unloaded');
    event.target.removeAttribute('data-cc-hidden');
    event.target.removeAttribute('aria-hidden');
    const controller = event.target.matches('[data-custom-collection-switcher]') ? event.target : event.target.querySelector('[data-custom-collection-switcher]');
    if (controller) selected.delete(normalize(controller.dataset.switchGroup));
    refresh();
  });
  document.addEventListener('shopify:section:unload', event => {
    event.target.setAttribute('data-cc-unloaded', '');
    scheduleRefresh();
  });
  document.addEventListener('shopify:section:reorder', scheduleRefresh);
  document.addEventListener('shopify:section:select', revealEditorSelection);
  document.addEventListener('shopify:block:select', revealEditorSelection);
  window.customCollectionTabs = { refresh: scheduleRefresh, select: activate, step };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
})();
